import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PythonTreeSitterReadyStatus } from '../../../hooks/editor/usePythonTreeSitterReady';

export interface TreeSitterInputBindingStatusProps {
  status: PythonTreeSitterReadyStatus;
}

/**
 * Compact Control-panel status for Tree-sitter WASM init and post-ready binding hydrate.
 * Browse stays available; mutation controls should be gated via `status.mappingsReady`.
 */
const TreeSitterInputBindingStatus: React.FC<TreeSitterInputBindingStatusProps> = ({ status }) => {
  const { t } = useTranslation();

  if (status.mappingsReady) return null;

  if (status.loading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 0.5, py: 0.75 }}
        role="status"
        aria-live="polite"
        data-testid="tree-sitter-input-binding-loading"
      >
        <CircularProgress size={16} aria-label={t('controller.buttonMapping.treeSitter.loadingAriaLabel', 'Loading input mapping tools')} />
        <Typography variant="body2" color="text.secondary">
          {t('controller.buttonMapping.treeSitter.loading', 'Loading input mapping tools…')}
        </Typography>
      </Stack>
    );
  }

  if (status.failed) {
    return (
      <Box data-testid="tree-sitter-input-binding-failed">
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={status.retry}>
              {t('controller.buttonMapping.treeSitter.retry', 'Retry')}
            </Button>
          }
        >
          {t(
            'controller.buttonMapping.treeSitter.failed',
            'Input mapping tools could not load. You can keep browsing; retry to edit or delete mappings.'
          )}
        </Alert>
      </Box>
    );
  }

  return null;
};

export default TreeSitterInputBindingStatus;
